<template>
  <div>
    <CRow class="justify-content-between mb-3">
      <CCol sm="auto"
        ><div class="ui-type-display-lg">
          {{ $t("family.settings.addresses.title") }}
        </div>
      </CCol>
      <CCol sm="auto">
        <CButtonGroup class="mx-auto">
          <CInput
            horizontal
            v-model="search"
            :placeholder="$t('role.profile.tab_images.search_placeholder')"
          >
            <template #append-content><CIcon name="cil-filter" /> </template>
          </CInput>
        </CButtonGroup>
        <CButton color="primary" @click="onAddAddress()" class="mb-3">
          {{ $t("family.settings.addresses.btn_title") }}
        </CButton>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="auto">
        <p>
          {{
            $t("family.settings.addresses.intro", [
              this.$store.state.connections.current.family.name,
            ])
          }}
        </p></CCol
      >
    </CRow>

    <CRow class="justify-content-start mb-3" v-if="loading">
      <CCol sm="3">
        <CSpinner color="primary" />
      </CCol>
    </CRow>

    <CRow class="justify-content-start mb-3" v-if="!loading">
      <CCol sm="3" v-for="(item, index) in filteredAddresses" :key="index">
        <CCard class="p-0">
          <CCardBody class="p-0">
            <!-- Show Map Only If Geocoded Data Exists -->
            <div
              v-if="geocodedAddresses[item.id]"
              class="rounded-top overflow-hidden"
            >
              <LMap
                :zoom="13"
                :options="{
                  zoomControl: false,
                  scrollWheelZoom: false,
                  doubleClickZoom: false,
                  touchZoom: false,
                  dragging: false,
                }"
                :center="[
                  geocodedAddresses[item.id].lat,
                  geocodedAddresses[item.id].lng,
                ]"
                style="height: 150px; width: 100%"
              >
                <LTileLayer
                  :url="cartoTileUrl"
                  attribution='&copy; <a href="https://stamen.com">Stamen Design</a>, &copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
                />
                <LMarker
                  :lat-lng="[
                    geocodedAddresses[item.id].lat,
                    geocodedAddresses[item.id].lng,
                  ]"
                  :icon="customMarkerIcon"
                />
              </LMap>
            </div>
            <p
              v-else
              style="
                height: 150px;
                background-color: #f0f0f0;
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: 0;
                padding: 0;
              "
            >
              <CSpinner color="primary" />
            </p>
            <div class="pt-1 px-3 pb-3">
              <div
                class="
                  d-flex
                  flex-row
                  w-100
                  justify-content-between
                  align-items-center
                  mt-2
                  mb-1
                "
              >
                <div>
                  <CLink
                    v-c-tooltip="
                      item.default_bill
                        ? $t('family.settings.addresses.defaultBilling')
                        : ''
                    "
                  >
                    <span
                      :class="[
                        item.default_bill ? 'text-primary' : 'text-gray-400',
                        'ml-2',
                      ]"
                    >
                      <font-awesome-icon :icon="['far', 'money-bill-alt']" />
                    </span>
                  </CLink>

                  <CLink
                    v-c-tooltip="
                      item.default_bill
                        ? $t('family.settings.addresses.defaultShipping')
                        : ''
                    "
                  >
                    <span
                      :class="[
                        item.default_ship ? 'text-primary' : 'text-gray-400',
                        'ml-2',
                      ]"
                    >
                      <font-awesome-icon :icon="['fas', 'truck']" />
                    </span>
                  </CLink>
                </div>
                <div>
                  <CLink v-c-tooltip="$t('family.settings.addresses.edit')">
                    <a
                      @click="onEditAddress(item)"
                      href="#"
                      class="ml-2 text-primary"
                    >
                      <font-awesome-icon :icon="['fas', 'edit']" />
                    </a>
                  </CLink>
                  <CLink v-c-tooltip="$t('family.settings.addresses.delete')">
                    <a
                      @click="removeAddress(item.id)"
                      href="#"
                      class="ml-2 text-primary"
                    >
                      <font-awesome-icon :icon="['far', 'trash-alt']" />
                    </a>
                  </CLink>
                </div>
              </div>
              <div
                class="d-flex flex-row justify-content-start align-items-center"
              >
                <p class="m-0 ml-2 mt-2">
                  <strong>{{ item.to }} </strong><br />
                  {{ item.address }} <br />
                  {{ item.postal_code.code }} <br />
                  {{ item.country }}
                </p>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <AddressesModal
      :show.sync="modal.show"
      :isEdit="modal.isEdit"
      :title="modal.title"
      :values="modal.values"
    />
  </div>
</template>
<script>
import AddressesModal from "./AddressesModal";
import errorResponse from "../../../helpers/error";
import EventBus from "../../../helpers/EventBus";
import { apiGet, apiDelete } from "../../../helpers/apiHelper";
import { LMap, LTileLayer, LMarker, LIcon } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CIcon } from "@coreui/icons-vue";
import { geocodeAddress } from "../../../helpers/common";
import { getCartoTileUrl } from "../../../config/mapTiles";

export default {
  components: {
    AddressesModal,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    CIcon,
  },

  data() {
    const familyId = this.$store.state.connections.current.family.id;
    return {
      loading: true,
      search: "",
      addressData: [],
      geocodedAddresses: {},
      customMarkerIcon: L.icon({
        iconUrl: "/loonity/img/locationpin.svg",
        iconSize: [32, 37],
        iconAnchor: [16, 37],
      }),
      iconSize: 64,
      error: errorResponse(),
      modal: {
        show: false,
        isEdit: false,
        title: this.$t("family.settings.addresses.modal.title"),
        values: {},
      },
    };
  },

  mounted() {
    EventBus.$on("address:remove", this.onRemoveAddress);
    EventBus.$on("address:edit", this.onEditAddress);
    EventBus.$on("address:refresh", this.onRefresh);
  },

  beforeDestroy() {
    EventBus.$off("address:remove", this.onRemoveAddress);
    EventBus.$off("address:edit", this.onEditAddress);
    EventBus.$off("address:refresh", this.onRefresh);
  },

  computed: {
    cartoTileUrl() {
      return getCartoTileUrl();
    },
    filteredAddresses() {
      if (!this.search) return this.addressData;

      const searchTerm = this.search.toLowerCase();

      return this.addressData.filter((item) => {
        const { address, city, to } = item;

        return (
          address.toLowerCase().includes(searchTerm) ||
          city.toLowerCase().includes(searchTerm) ||
          to.toLowerCase().includes(searchTerm)
        );
      });
    },
  },

  methods: {
    onAddAddress() {
      this.modal.title = this.$t("family.settings.addresses.modal.title");
      this.modal.isEdit = false;
      this.modal.values = {};
      this.modal.show = true;
    },

    onEditAddress(item) {
      this.modal.title = this.$t("family.settings.addresses.modal.edit.title");
      this.modal.isEdit = true;
      this.modal.values = { ...item };
      this.modal.show = true;
    },

    onRefresh() {
      this.fetchData([this.$store.state.connections.current.family.id]);
    },

    async fetchData(familyId) {
      const response = await apiGet("auth_faddress", familyId);
      if (response && response.data) {
        this.loading = false;
        this.addressData = response.data.payload.Items;

        // After fetching addresses, geocode each address, if lat and lng are not already present
        for (const item of this.addressData) {
          if (item.latitude && item.longitude) {
            this.$set(this.geocodedAddresses, item.id, {
              lat: item.latitude,
              lng: item.longitude,
            });
            continue;
          }
          const geocodedAddress = await geocodeAddress(item);
          this.$set(this.geocodedAddresses, item.id, geocodedAddress);
        }
      }
    },

    async removeAddress(id) {
      const response = await apiDelete("auth_faddress", id, [
        this.$store.state.connections.current.family.id,
      ]);
      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("family.settings.addresses.success"),
          color: "success",
          autohide: true,
        });
        await this.fetchData([this.$store.state.connections.current.family.id]);
      } else if (response && response.errors) {
        this.error.set(response.errors);
      } else {
        console.error("Unexpected response:", response);
      }
    },
  },

  async created() {
    this.loading = true;
    await this.fetchData([this.$store.state.connections.current.family.id]);
  },
};
</script>
